import { useEffect, useRef, useState } from "react";
import type { PartyResponse, VideoPartyResponse } from "@studio/shared";
import { TeacherMessagePage } from "../components/TeacherChrome";
import { navigate } from "../lib/navigation";
import { watchPlaybackStall } from "../lib/playbackStall";
import {
  TeacherApiError,
  TeacherLoginRequiredError,
  teacherRequest,
} from "../lib/teacherApi";

type AnyPartyResponse = PartyResponse | VideoPartyResponse;

export function PartyPage({ sessionId }: { sessionId: string }) {
  const [party, setParty] = useState<AnyPartyResponse | null>(null);
  const [fatal, setFatal] = useState<"login" | "forbidden" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void teacherRequest<AnyPartyResponse>(`/api/sessions/${encodeURIComponent(sessionId)}/party`)
      .then(setParty)
      .catch((caught: unknown) => {
        if (caught instanceof TeacherLoginRequiredError) setFatal("login");
        else if (caught instanceof TeacherApiError && caught.status === 403) setFatal("forbidden");
        else setError("감상회 작품을 불러오지 못했어요.");
      });
  }, [sessionId]);

  if (fatal) return <TeacherMessagePage kind={fatal} />;
  if (!party) {
    return (
      <main className="party-page">
        <PartyHeader sessionId={sessionId} label="우리 반 감상회" />
        <p className="party-loading">감상회 무대를 준비하는 중...</p>
        {error ? <div className="party-error" role="alert">{error}</div> : null}
      </main>
    );
  }
  return "videos" in party
    ? <VideoParty sessionId={sessionId} party={party} />
    : <MusicParty sessionId={sessionId} party={party} />;
}

function PartyHeader({
  sessionId,
  label,
  count,
}: {
  sessionId: string;
  label: string;
  count?: string;
}) {
  return (
    <header className="party-header">
      <button type="button" onClick={() => navigate(`/teacher/live/${encodeURIComponent(sessionId)}`)}>← 라이브 현황</button>
      <span>{label}</span>
      {count ? <b>{count}</b> : <span />}
    </header>
  );
}

/**
 * 감상회에서 쓸 **재생 주소**를 자산 id 로 그때그때 받아 온다.
 *
 * 🚨 실패를 삼키지 않는다. 종전 음악 감상회는 이 요청이 실패해도 화면이 그대로였고
 *    (버튼이 「재생 준비 중」 에 멈춘 채) 왜 안 되는지 말할 수단이 아예 없었다 —
 *    그래서 prod 에서 원인을 좁힐 수조차 없었다(2026-08-18 jery 신고). 이제 상태코드를
 *    사람 말과 함께 남기고, 개발자도구에도 한 줄 찍고, 다시 시도할 손잡이를 준다.
 *
 * 🔑 «그때그때» 가 핵심이다. 영상 감상회는 페이지를 열 때 모든 작품을 한 번에 10분짜리로
 *    서명해 뒀는데, 한 반을 다 보는 데 10분이 넘으므로 뒤쪽 작품은 반드시 403 이 됐다.
 *    작품을 넘길 때마다 새로 받으면 감상회 길이와 무관해진다.
 */
function useSignedAssetUrl(assetId: string | null, initialUrl: string | null = null) {
  const [url, setUrl] = useState<string | null>(initialUrl);
  const [failure, setFailure] = useState<string | null>(null);
  // 재시도 횟수는 **자산별로** 센다 — 작품을 넘기면 다시 0 이어야 «즉시 재생용 주소» 를
  // 쓸 수 있고, 앞 작품에서 재시도했다는 이유로 다음 작품이 느려지지 않는다.
  const [attempts, setAttempts] = useState<{ id: string | null; count: number }>({ id: assetId, count: 0 });
  const attempt = attempts.id === assetId ? attempts.count : 0;

  useEffect(() => {
    // 🚨 다시 시도할 때는 `initialUrl` 로 되돌리지 **않는다.** 그 주소는 이미 만료됐을 수
    //    있고, 되돌리면 <video> 가 다시 onError → 다시 재시도로 맴돈다(무한 루프).
    setUrl(attempt === 0 ? initialUrl : null);
    setFailure(null);
    if (!assetId) return undefined;
    let active = true;
    void teacherRequest<{ url: string }>(`/api/assets/${encodeURIComponent(assetId)}/url`)
      .then((result) => {
        if (!active) return;
        setUrl(result.url);
        setFailure(null);
      })
      .catch((caught: unknown) => {
        if (!active) return;
        const detail = caught instanceof TeacherApiError
          ? `${caught.status} ${caught.code}`
          : caught instanceof TeacherLoginRequiredError
            ? "401 AUTH_REQUIRED"
            : "네트워크";
        // 🔑 원인을 여기서 단정하지 않는다. 상태코드를 그대로 남겨야 다음 수업에서
        //    「권한인지·파일이 없는지·서버 오류인지」가 한 화면에서 갈린다.
        console.error("[party] 재생 주소를 받지 못했다", { assetId, detail, caught });
        setFailure(detail);
      });
    return () => { active = false; };
  }, [assetId, initialUrl, attempt]);

  return { url, failure, retry: () => setAttempts({ id: assetId, count: attempt + 1 }) };
}

/**
 * 재생을 누른 뒤 **소리도 그림도 시작되지 않는 시간**을 지켜본다.
 *
 * 🚨 `onError` 만으로는 절반만 잡힌다(2026-08-18 prod 실측). 그날 잡힌 실패는 에러를
 *    내지 않았다 — `readyState 0` 인 채로 10초가 지나도 아무 사건도 오지 않았고,
 *    버튼만 「일시정지」로 바뀌어 **재생 중인 것처럼 보였다.** 시계가 없으면 그 화면에는
 *    빨간 상자도, 상태코드도, 다시 시도할 손잡이도 뜨지 않는다.
 *
 * 🔑 여기서 자동으로 다시 받지 않는다. 멈춤의 원인은 «주소 만료» 뿐 아니라 «간헐적 5xx»
 *    일 수도 있어서, 자동 재시도는 같은 자리를 맴돌 수 있다. 교사에게 「다시 시도」 를
 *    한 번 누르게 하는 편이 예측 가능하다 — 그 버튼은 새 서명을 받는다.
 */
/**
 * 멈춤에는 HTTP 상태코드가 없다 — 응답이 아예 오지 않은 실패이기 때문이다.
 * 그 자리에 **재생기의 계기판**을 대신 남긴다. 다음 사람이 화면 사진만으로 갈라 볼 수 있게.
 */
function describeStall(media: HTMLMediaElement): string {
  return `정지 ready=${media.readyState} net=${media.networkState} t=${media.currentTime.toFixed(1)}`;
}

function usePlaybackStall(
  ref: { current: HTMLMediaElement | null },
  waiting: boolean,
  onStall: (media: HTMLMediaElement) => void,
): void {
  // 최신 콜백을 쓰되 감시를 다시 걸지는 않는다 — 매 렌더마다 시계가 되감기면 영영 안 울린다.
  const latest = useRef(onStall);
  latest.current = onStall;
  useEffect(() => {
    const media = ref.current;
    if (!waiting || !media) return undefined;
    return watchPlaybackStall(media, () => latest.current(media));
  }, [ref, waiting]);
}

function VideoParty({ sessionId, party }: { sessionId: string; party: VideoPartyResponse }) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  // 「눌렀는데 아직 안 나온다」 = 재생과도 정지와도 다른 제3의 상태. 이걸 구분하지 않아서
  // 버튼이 「일시정지」로 바뀐 채 아무 소리도 안 나는 화면이 나왔다.
  const [waiting, setWaiting] = useState(false);
  const [stalled, setStalled] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // 작품 하나당 자동 재시도는 **한 번뿐**이다. 그 뒤로는 사람이 「다시 시도」를 누른다.
  const autoRecovered = useRef<number | null>(null);
  const video = party.videos[index] ?? null;
  const nextVideo = party.videos.length
    ? party.videos[(index + 1) % party.videos.length]
    : null;
  // 🔑 작품을 넘길 때마다 새 서명을 받는다. 응답에 실린 `video_url` 은 «지금 당장 쓸» 주소로
  //    먼저 쓰고, 새 주소가 오면 갈아 끼운다 — 첫 재생이 느려지지 않는다.
  const { url: videoUrl, failure, retry } = useSignedAssetUrl(
    video?.asset_id ?? null,
    video?.video_url ?? null,
  );

  usePlaybackStall(videoRef, waiting, (media) => {
    const detail = describeStall(media);
    console.error("[party] 영상이 시작되지 않았다", { assetId: video?.asset_id, detail });
    setWaiting(false);
    setStalled(detail);
    setError("영상이 시작되지 않았어요. 「다시 시도」를 눌러 주세요.");
  });

  useEffect(() => {
    setPlaying(false);
    setWaiting(false);
    setStalled(null);
    setError(null);
  }, [index]);

  const move = (next: number): void => {
    if (!party.videos.length) return;
    videoRef.current?.pause();
    setIndex((next + party.videos.length) % party.videos.length);
  };

  return (
    <main className="party-page mv-party-page">
      <PartyHeader
        sessionId={sessionId}
        label="VT3 · 우리 반 뮤직비디오 감상회"
        count={party.videos.length ? `${index + 1} / ${party.videos.length}` : ""}
      />
      {party.videos.length === 0 ? (
        <section className="party-empty mv-party-empty">
          <span aria-hidden="true">▻</span>
          <h1>완성한 뮤직비디오가 아직 없어요</h1>
          <p>학생이 뮤직비디오를 완성하면 여기에 나타나요.</p>
          {/* 🚨 이 문장 하나로는 «아직 아무도 안 만들었다» 와 «만들었는데 안 실렸다» 가
              구분되지 않는다(2026-08-13 jery). 완성작이 실제로 있는데 비어 있을 때만
              어디서 걸렸는지 교사에게 알려 준다 — 학생 화면에는 나오지 않는 경로다. */}
          {party.diagnostic && party.diagnostic.works_with_final > 0 ? (
            <p className="party-empty-diagnostic">
              완성한 영상 {party.diagnostic.works_with_final}편 중{" "}
              {party.diagnostic.assets_resolved}편의 영상 파일이 확인돼요.
              {" 영상 파일을 찾지 못했어요 — 선생님께 이 화면을 알려 주세요."}
            </p>
          ) : null}
        </section>
      ) : null}
      {video ? (
        <>
          <section className="mv-party-stage">
            <div className="mv-party-video-frame">
              <video
                ref={videoRef}
                src={videoUrl ?? undefined}
                preload="metadata"
                aria-label={`${video.title} · ${video.student_name}`}
                // 🚨 `onPlay` 가 아니라 `onPlaying` 이다. `onPlay` 는 «틀어 달라고 말한 순간»
                //    이라 그림이 한 장도 안 나온 채로도 온다 — 그걸 재생으로 표시해서
                //    버튼이 거짓말을 했다. `onPlaying` 은 실제로 나오기 시작해야 온다.
                onPlaying={() => { setPlaying(true); setWaiting(false); }}
                onPause={() => { setPlaying(false); setWaiting(false); }}
                onEnded={() => { setPlaying(false); setWaiting(false); }}
                // 🚨 「다음 작품으로 넘겨 주세요」는 교사에게 **포기하라**는 말이었다.
                //    주소 만료가 가장 흔한 원인이므로 그 자리에서 새 주소를 받아 본다.
                onError={() => {
                  setWaiting(false);
                  if (autoRecovered.current === index) {
                    setError("영상을 재생하지 못했어요. 「다시 시도」를 눌러 주세요.");
                    return;
                  }
                  autoRecovered.current = index;
                  setError("영상을 불러오지 못했어요. 새 주소로 다시 받는 중이에요.");
                  retry();
                }}
              />
            </div>
            <div className="mv-party-info">
              <h1>{video.title}</h1>
              <p>{video.student_name} · {formatDuration(video.duration)} · 720p</p>
            </div>
          </section>
          <footer className="party-controls mv-party-controls">
            <div className="party-player">
              <button type="button" onClick={() => move(index - 1)}>이전</button>
              <button
                className="party-pause-button"
                type="button"
                disabled={!videoUrl}
                onClick={() => {
                  const player = videoRef.current;
                  if (!player) return;
                  if (player.paused) {
                    setStalled(null);
                    setWaiting(true);
                    void player.play().catch(() => {
                      setWaiting(false);
                      setError("재생 버튼을 다시 눌러 주세요.");
                    });
                  } else {
                    setWaiting(false);
                    player.pause();
                  }
                }}
              >{!videoUrl ? "재생 준비 중" : waiting ? "불러오는 중" : playing ? "일시정지" : "재생"}</button>
              <button type="button" onClick={() => move(index + 1)}>다음</button>
            </div>
            <p>{nextVideo ? `다음: ${nextVideo.student_name} · ${nextVideo.title}` : "마지막 작품이에요"}</p>
            <button className="party-exit-button" type="button" onClick={() => navigate(`/teacher/live/${encodeURIComponent(sessionId)}`)}>감상회 마치기</button>
          </footer>
        </>
      ) : null}
      <PartyFailure
        message={error}
        detail={failure ?? stalled}
        onRetry={() => { setError(null); setStalled(null); retry(); }}
      />
    </main>
  );
}

function MusicParty({ sessionId, party }: { sessionId: string; party: PartyResponse }) {
  const [index, setIndex] = useState(0);
  const [lyricIndex, setLyricIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [stalled, setStalled] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const track = party.tracks[index] ?? null;
  const { url: audioUrl, failure, retry } = useSignedAssetUrl(track?.asset_id ?? null);
  // 주소를 못 받았거나 재생이 실패한 상태 = 큰 버튼만으로는 빠져나갈 수 없는 상태.
  const blocked = Boolean(failure) || Boolean(error);

  usePlaybackStall(audioRef, waiting, (media) => {
    const detail = describeStall(media);
    console.error("[party] 노래가 시작되지 않았다", { assetId: track?.asset_id, detail });
    setWaiting(false);
    setStalled(detail);
    setError("노래가 시작되지 않았어요. 「다시 시도」를 눌러 주세요.");
  });

  useEffect(() => {
    setLyricIndex(0);
    setPlaying(false);
    setWaiting(false);
    setStalled(null);
    setError(null);
  }, [index]);

  const lines = track?.lyrics.split(/\r?\n/u).map((line) => line.trim()).filter(Boolean) ?? [];
  const nextTrack = party.tracks.length ? party.tracks[(index + 1) % party.tracks.length] : null;
  const move = (next: number): void => {
    if (!party.tracks.length) return;
    audioRef.current?.pause();
    setIndex((next + party.tracks.length) % party.tracks.length);
  };

  return (
    <main className="party-page">
      <PartyHeader
        sessionId={sessionId}
        label="MT3 · 우리 반 감상회"
        count={party.tracks.length ? `${index + 1} / ${party.tracks.length}` : ""}
      />
      {party.tracks.length === 0 ? (
        <section className="party-empty">
          <span aria-hidden="true">♪</span>
          <h1>아직 완성된 노래가 없어요</h1>
          <p>학생들이 노래를 완성하고 감상회 공개에 동의하면 여기에 나타나요.</p>
        </section>
      ) : null}
      {track ? (
        <>
          <section className="party-stage">
            <div className="party-song-info">
              <h1>{track.title}</h1>
              <p><span className="party-student">{track.student_name}</span> · {formatDuration(track.duration_seconds)}</p>
            </div>
            <div className="party-wave" aria-hidden="true">
              {[18, 40, 60, 28, 68, 44, 22, 52, 66, 32, 56, 24, 46, 62, 20, 48, 30, 58].map((height, waveIndex) => (
                <i key={`${height}-${waveIndex}`} style={{ "--party-wave-height": `${height}px` } as React.CSSProperties} />
              ))}
            </div>
            <div className="party-lyrics" aria-live="polite">
              {lines.length
                ? lines.map((line, lineIndex) => <p className={lineIndex === lyricIndex ? "current" : ""} key={`${line}-${lineIndex}`}>{line}</p>)
                : <p className="current">가사 없이 멜로디로 만든 곡이에요</p>}
              <small>지금 나오는 가사</small>
            </div>
          </section>
          <footer className="party-controls">
            <div className="party-player">
              <button type="button" onClick={() => move(index - 1)}>이전 곡</button>
              {/*
                🔑 평소에는 숨긴다 — 감상회는 프로젝터 화면이고, 큰 버튼 하나가 유일한
                   재생 손잡이여야 한다(«재생처럼 생긴 것은 하나뿐» 계약).
                   다만 **막혔을 때는 드러낸다.** 교사에게 남는 우회로가 하나는 있어야
                   수업이 멈추지 않는다.
              */}
              <audio
                ref={audioRef}
                className={blocked ? "party-audio-fallback" : "party-audio"}
                controls={blocked}
                src={audioUrl ?? undefined}
                // 🚨 `onPlay`(틀어 달라고 말한 순간)가 아니라 `onPlaying`(실제로 나오기
                //    시작한 순간)이다 — 그래야 버튼이 거짓말을 하지 않는다.
                onPlaying={() => { setPlaying(true); setWaiting(false); }}
                onPause={() => { setPlaying(false); setWaiting(false); }}
                onEnded={() => move(index + 1)}
                onError={() => { setWaiting(false); setError("노래 파일을 열지 못했어요."); }}
                onTimeUpdate={(event) => {
                  if (!lines.length) return;
                  const audio = event.currentTarget;
                  const duration = Number.isFinite(audio.duration) && audio.duration > 0
                    ? audio.duration
                    : track.duration_seconds;
                  setLyricIndex(Math.min(lines.length - 1, Math.floor((audio.currentTime / duration) * lines.length)));
                }}
              />
              <button
                className="party-pause-button"
                type="button"
                disabled={!audioUrl}
                onClick={() => {
                  const audio = audioRef.current;
                  if (!audio) return;
                  // 🚨 종전에는 `void audio.play()` 였다 — 실패해도 화면에 아무 일이
                  //    일어나지 않아, 교사에게는 «버튼이 죽었다» 로만 보였다.
                  if (audio.paused) {
                    setStalled(null);
                    setWaiting(true);
                    void audio.play().catch(() => {
                      setWaiting(false);
                      setError("재생 버튼을 다시 눌러 주세요.");
                    });
                  } else {
                    setWaiting(false);
                    audio.pause();
                  }
                }}
              >{!audioUrl ? "재생 준비 중" : waiting ? "불러오는 중" : playing ? "일시정지" : "재생"}</button>
              <button type="button" onClick={() => move(index + 1)}>다음 곡</button>
            </div>
            <p>{nextTrack ? `다음: ${nextTrack.student_name} · ${nextTrack.title}` : "마지막 곡이에요"}</p>
            <button className="party-exit-button" type="button" onClick={() => navigate(`/teacher/live/${encodeURIComponent(sessionId)}`)}>감상회 마치기</button>
          </footer>
        </>
      ) : null}
      <PartyFailure
        message={error}
        detail={failure ?? stalled}
        onRetry={() => { setError(null); setStalled(null); retry(); }}
      />
    </main>
  );
}

/**
 * 감상회에서 «막혔다» 를 말하는 유일한 자리 — 사람 말 + 상태코드 + 다시 시도가 한 덩어리다.
 *
 * 🔑 셋을 쪼개면 안 된다. 문구만 있으면 교사는 무엇을 할지 모르고, 코드만 있으면
 *    무슨 일인지 모르고, 다시 시도가 없으면 수업이 거기서 멈춘다.
 */
function PartyFailure({
  message,
  detail,
  onRetry,
}: {
  message: string | null;
  detail: string | null;
  onRetry: () => void;
}) {
  if (!message && !detail) return null;
  return (
    <div className="party-error" role="alert">
      <span>{message ?? "재생 주소를 불러오지 못했어요."}</span>
      {detail ? <code className="party-error-detail">{detail}</code> : null}
      <button type="button" onClick={onRetry}>다시 시도</button>
    </div>
  );
}

function formatDuration(value: number): string {
  const seconds = Math.max(0, Math.round(value));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
